import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { buttonClasses } from '@agnivo/ui';
import { EmptyState } from '../components/EmptyState';
import { useAppConfig } from '../config';
import { getMessages } from '../i18n';
import { identityGapBotUrl } from './identityGap';
const t = getMessages();
/**
 * Gate for the IDENTITY_NOT_PROVISIONED state: valid session, no platform
 * identity, so every call fails and nothing in the app works.
 *
 * FULL-SCREEN replacement, not an inline banner. The state is total — there is no
 * subset of the shell that still functions — so leaving the nav and an empty chat
 * around the message would present a working app that isn't one.
 *
 * Shell wiring (mirrors the mini-app's tg_uid-missing precedent):
 *
 *   const gap = useIdentityGap();
 *   <ApiProvider onApiError={gap.onApiError}>
 *     <IdentityGapGate gap={gap} surface="gap_web">
 *       <RouterProvider router={router} />
 *
 * app-core owns no policy about WHICH surface this is — the shell names itself,
 * because the same card renders on web-UI and desktop and a hardcoded param would
 * under-count one of them from day one.
 */
export function IdentityGapGate({ gap, surface, children, }) {
    const { openExternal, botChatUrl } = useAppConfig();
    if (!gap.gapped)
        return _jsx(_Fragment, { children: children });
    const url = identityGapBotUrl(surface, botChatUrl);
    return (
    // data-identity-gap marks this state for automation. It is otherwise
    // indistinguishable from "the app failed to render": the gate REPLACES the whole
    // tree, so from the outside a gapped app and a crashed one look the same — an
    // absent shell either way. The desktop live-login e2e asserts on it for exactly
    // that reason (see liveLogin.e2e.ts), having spent a red proving the point.
    _jsx("div", { "data-identity-gap": "true", className: "flex h-full flex-col items-center justify-center p-6", children: _jsx(EmptyState, { icon: 
            // Restrained outline glyph, not a mascot (identity anti-pattern) and
            // not a warning triangle — product: this is an expected sequencing
            // gap, not a fault, and error iconography would misrepresent it.
            _jsx("svg", { viewBox: "0 0 24 24", width: "24", height: "24", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M12 2.5l1.7 5.9 5.9 1.6-5.9 1.7L12 17.5l-1.7-5.8-5.9-1.7 5.9-1.6z" }) }), title: t.identityGap.title, body: t.identityGap.body, action: 
            // A real <a href> so it is a link to the browser, the keyboard and a
            // screen reader — with the shell-native opener layered on top where the
            // host provides one. Order matters: on a surface whose webview will not
            // hand an external URL to the OS (desktop's custom-protocol window,
            // Telegram's WebView), the bare <a> is a DEAD CONTROL, and this card's
            // only job is this one link. preventDefault only when we have somewhere
            // better to send it.
            _jsx("a", { href: url, onClick: openExternal
                    ? (e) => {
                        e.preventDefault();
                        openExternal(url);
                    }
                    : undefined, className: buttonClasses('primary'), "data-testid": "identity-gap-cta", children: t.identityGap.cta }) }) }));
}
//# sourceMappingURL=IdentityGapGate.js.map